import React from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ToggleRow from "../../../../common/ToggleRow";
import AgentConfigSection from "../../AgentConfigSection";
import ModelCardPicker from "../../../../common/ModelCardPicker";
import ValidationOptionCards from "./ValidationOptionCards";

const TRIGGER_OPTIONS = [
  {
    value: "per_turn",
    label: "Every turn",
    description: "Validate after each agent response.",
    Icon: RepeatOutlinedIcon,
  },
  {
    value: "on_completion",
    label: "On completion",
    description: "Validate only when the agent finishes the task.",
    Icon: FlagOutlinedIcon,
  },
];

const TYPE_OPTIONS = [
  {
    value: "llm",
    label: "LLM judge",
    description: "An LLM checks output against your completion criteria.",
    Icon: PsychologyOutlinedIcon,
  },
  {
    value: "deterministic",
    label: "Regex",
    description: "Pass when the output matches a regex pattern.",
    Icon: CodeOutlinedIcon,
  },
];

const ON_FAIL_OPTIONS = [
  { value: "retry", label: "Retry with feedback" },
  { value: "fail", label: "Mark session failed" },
  { value: "continue", label: "Continue anyway" },
];

export default function OutputValidationSection({
  validation,
  setValidationField,
}) {
  const slice = validation || {};
  const type = slice.type || "llm";
  const fieldsDisabled = !slice.enabled;

  return (
    <AgentConfigSection id="outputValidation">
      <ToggleRow
        checked={!!slice.enabled}
        onChange={(checked) => setValidationField("enabled", checked)}
        title="Validate agent output"
        description="Check each response before it is finalized, with automatic retry."
      />

      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
          ...(fieldsDisabled && {
            opacity: 0.55,
            pointerEvents: "none",
          }),
        }}
        aria-disabled={fieldsDisabled}
      >
          <Grid item xs={12}>
            <Typography className="org-field-label">When to validate</Typography>
            <ValidationOptionCards
              ariaLabel="When to validate"
              disabled={fieldsDisabled}
              options={TRIGGER_OPTIONS}
              value={slice.trigger || "per_turn"}
              onChange={(next) => setValidationField("trigger", next)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography className="org-field-label">Validation type</Typography>
            <ValidationOptionCards
              ariaLabel="Validation type"
              disabled={fieldsDisabled}
              options={TYPE_OPTIONS}
              value={type}
              onChange={(next) => setValidationField("type", next)}
            />
          </Grid>

          {type === "llm" && (
            <Grid item xs={12}>
              <Typography className="org-field-label">Validation model</Typography>
              <ModelCardPicker
                className="org-styled-textfield"
                allowDefault
                defaultLabel="Fast (default)"
                defaultDescription="Uses a fast, cost-efficient model optimized for validation checks."
                value={slice.validationModel}
                onChange={(nextModel) =>
                  setValidationField("validationModel", nextModel)
                }
              />
            </Grid>
          )}

          {type === "llm" && (
            <Grid item xs={12}>
              <Typography className="org-field-label">
                Completion criteria
              </Typography>
              <TextField
                className="org-styled-textfield"
                fullWidth
                multiline
                minRows={3}
                size="small"
                disabled={fieldsDisabled}
                value={slice.completionCriteria || ""}
                onChange={(e) =>
                  setValidationField("completionCriteria", e.target.value)
                }
                placeholder="Describe when a response should be considered complete and correct..."
              />
            </Grid>
          )}

          {type === "deterministic" && (
            <Grid item xs={12}>
              <Typography className="org-field-label">Regex pattern</Typography>
              <TextField
                className="org-styled-textfield"
                fullWidth
                size="small"
                disabled={fieldsDisabled}
                value={slice.validationCode || ""}
                onChange={(e) =>
                  setValidationField("validationCode", e.target.value)
                }
                placeholder="e.g. approved|confirmed"
              />
            </Grid>
          )}

          <Grid item xs={4}>
            <Typography className="org-field-label">Max retries</Typography>
            <TextField
              className="org-styled-textfield"
              type="number"
              fullWidth
              size="small"
              disabled={fieldsDisabled}
              value={slice.maxRetries ?? 3}
              onChange={(e) => {
                const next = Math.max(
                  0,
                  Math.min(10, parseInt(e.target.value, 10) || 0)
                );
                setValidationField("maxRetries", next);
              }}
              inputProps={{ min: 0, max: 10 }}
            />
          </Grid>

          <Grid item xs={8}>
            <Typography className="org-field-label">On failure</Typography>
            <TextField
              className="org-styled-textfield"
              select
              fullWidth
              size="small"
              disabled={fieldsDisabled}
              value={slice.onFail || "retry"}
              onChange={(e) => setValidationField("onFail", e.target.value)}
            >
              {ON_FAIL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <ToggleRow
              checked={!!slice.hideFromChat}
              onChange={(checked) =>
                setValidationField("hideFromChat", checked)
              }
              title="Hide validation from chat"
              description="Hide validation results and retried attempts from the transcript — viewers see only the final response. Recommended for public-facing agents."
            />
          </Grid>
      </Grid>
    </AgentConfigSection>
  );
}
